import React from "react";
import styled from "styled-components";

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 160px 40px 80px;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 100px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 60px;
    padding: 120px 20px 60px;
  }
`;

const StickyLeft = styled.div`
  position: sticky;
  top: 160px; /* Match PageWrapper padding-top to prevent initial 20px scroll snapping */
  height: fit-content;

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
  }
`;

const HugeTitle = styled.h1`
  font-size: var(--font-h1);
  font-weight: 900;
  line-height: 0.9;
  letter-spacing: -0.05em;
  margin-bottom: 40px;
  color: var(--text-color);

  span {
    color: var(--accent);
  }
`;

const BioText = styled.p`
  font-size: var(--font-lead);
  line-height: 1.6;
  opacity: 0.9;
  margin-bottom: 30px;
`;

const ContactBox = styled.div`
  margin-top: 60px;
  padding-left: 20px;
  border-left: 4px solid var(--accent);

  h3 {
    font-size: var(--font-body);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 20px;
    color: var(--text-color);
  }
`;

const ContactItem = styled.div`
  margin-bottom: 15px;

  span {
    display: block;
    font-weight: 700;
    font-size: var(--font-small);
    margin-bottom: 4px;
    color: var(--text-color);
  }

  p, a {
    margin: 0;
    font-size: var(--font-small);
    opacity: 0.7;
    text-decoration: none;
    color: var(--text-color);

    &:hover {
      color: var(--accent);
    }
  }
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 1px solid rgba(150, 150, 150, 0.2);
  padding-bottom: 16px;
  margin-bottom: 40px;

  h2 {
    font-size: var(--font-h3);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 0;
  }

  span {
    font-size: var(--font-meta);
    font-weight: 600;
    letter-spacing: 2px;
    opacity: 0.5;
    color: var(--accent);
  }
`;

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(150, 150, 150, 0.2);
  margin-left: 8px;
  padding-left: 40px;
  margin-bottom: 80px;
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 60px;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -44px;
    top: 4px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--bg-color);
    border: 2px solid var(--accent);
  }

  .year {
    position: absolute;
    right: 0;
    top: 0;
    font-size: var(--font-meta);
    font-weight: 600;
    color: var(--text-color);
    opacity: 0.5;
    letter-spacing: 1px;

    @media (max-width: 768px) {
      position: relative;
      margin-bottom: 8px;
      display: block;
    }
  }

  h3 {
    font-size: var(--font-lead);
    font-weight: 800;
    margin-bottom: 4px;
  }

  h4 {
    font-size: var(--font-meta);
    font-weight: 700;
    color: var(--accent);
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  ul {
    list-style-type: none;
    padding-left: 0;
    margin: 0;
    
    li {
      font-size: var(--font-body);
      line-height: 1.6;
      opacity: 0.8;
      margin-bottom: 12px;
      position: relative;
      padding-left: 20px;

      &::before {
        content: "•";
        color: var(--accent);
        position: absolute;
        left: 0;
      }
    }
  }

  .sub-role {
    margin-top: 24px;
    
    .label {
      font-size: var(--font-small);
      font-weight: 700;
      opacity: 0.9;
      margin-bottom: 12px;
    }
  }
`;

const SkillsBox = styled.div`
  background: var(--panel-bg);
  padding: 40px;
  border-radius: 6px;
  margin-bottom: 80px;

  h2 {
    font-size: var(--font-body);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 30px;
    color: var(--text-color);
  }
`;

const SkillRow = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  .level-header {
    display: flex;
    justify-content: space-between;
    font-size: var(--font-meta);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 12px;
    color: var(--text-color);
    opacity: 0.6;

    .percentage {
      color: var(--accent);
    }
  }

  .skill-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    span {
      font-size: var(--font-meta);
      font-weight: 600;
      padding: 6px 12px;
      background: var(--bg-color);
      border: 1px solid rgba(150, 150, 150, 0.2);
      border-radius: 4px;
      color: var(--text-color);
    }
  }
`;

const GridTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SimpleBlockList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .block-item {
    display: flex;
    flex-direction: column;

    h4 {
      font-size: var(--font-body);
      font-weight: 800;
      margin-bottom: 4px;
    }
    
    p {
      font-size: var(--font-small);
      opacity: 0.7;
    }
  }
`;

const About = () => {
  return (
    <PageWrapper>
      <StickyLeft>
        <HugeTitle>
          Stephen<br />
          SJ Brown
        </HugeTitle>
        <BioText>
          A Designer with 9 years experience and background in rapid prototyping and front-end engineering.
        </BioText>
        <BioText>
          My design process solves complex problems by driving cross functional collaboration, leading workshops, analysing mixed data, communicating challenges and strategy, and rapid iterative prototyping often using AI.
        </BioText>

        <ContactBox>
          <h3>Contact Info</h3>
          <ContactItem>
            <span>Location</span>
            <p>London, SE</p>
          </ContactItem>
          <ContactItem>
            <span>Email</span>
            <a href="mailto:stephen.sjbrown@gmail.com">stephen.sjbrown@gmail.com</a>
          </ContactItem>
          <ContactItem style={{ display: 'flex', gap: '20px', marginTop: '30px' }}>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, textDecoration: 'underline' }}>LinkedIn</a>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, textDecoration: 'underline' }}>GitHub</a>
          </ContactItem>
        </ContactBox>
      </StickyLeft>

      <RightContent>
        <SectionHeader>
          <h2>Experience & History</h2>
          <span>2016 — PRESENT</span>
        </SectionHeader>

        <TimelineWrapper>
          <TimelineItem>
            <span className="year">'23 — Present</span>
            <h3>UX Designer III</h3>
            <h4>GOOGLE (VIA ADECCO / XWF)</h4>
            <p style={{ fontSize: '0.9rem', marginBottom: '16px', opacity: 0.8 }}>GBO, Advertising and Marketing tools</p>
            <ul>
              <li>Influenced cross function collaborators and executed design strategy for public Guests, and internal Admin and Seller features</li>
              <li>Leveraged AI across a number of stages of the product lifecycle to enhance outputs where appropriate</li>
              <li>Led discovery, workshops, design, prototyping, and usability for 7 features releases using qualitative and quantitative input</li>
              <li>Presented design concepts to senior leadership, explaining rationale and gaining successful approval</li>
            </ul>
          </TimelineItem>

          <TimelineItem>
            <span className="year">'22 — '23</span>
            <h3>Lead Product Designer</h3>
            <h4>FETCH.AI</h4>
            <ul>
              <li>Led the first qualitative UX research for Wallet web extension to provide a user-focused foundation for future strategic design work</li>
              <li>Redesigned Wallet which resulted in a 17% increase in user adoption</li>
              <li>Create and maintain designs and develop features for Wallet and Block Explorer products, and developer and main product websites</li>
              <li>Engineer and deploy custom enhancements to products in React</li>
            </ul>
          </TimelineItem>

          <TimelineItem>
            <span className="year">'20</span>
            <h3>Developer and Designer</h3>
            <h4>EYESCAN</h4>
            <p style={{ fontSize: '0.9rem', marginBottom: '16px', opacity: 0.8 }}>Ophthalmology Diagnostic Proof of Concept</p>
            <ul>
              <li>Created high fidelity prototypes with Figma using tablet best practices enabling users to complete 3 key diagnostic tasks</li>
              <li>Developed designs in React and refactored team members' code for reusability and adding animation in 6 weeks</li>
              <li>Enabled 5 ophthalmologists to successfully complete diagnostic tasks with a 84% success rate, demonstrating the feasibility of the concept</li>
            </ul>
          </TimelineItem>

          <TimelineItem>
            <span className="year">'16 — '22</span>
            <h3>Product Designer</h3>
            <h4>ACCENTURE</h4>

            <div className="sub-role">
              <div className="label">EE (UK Telecom) – Enterprise Sales Application – Design Lead</div>
              <ul>
                <li>Create design-system patterns and documentation, collaborating with engineering to increase consistency and reduce review time by 33%</li>
                <li>Review 6 designers work to feedback and sign-off for fortnightly sprints</li>
                <li>Run 9 user feedback sessions and presentations to senior stakeholders</li>
              </ul>
            </div>

            <div className="sub-role">
              <div className="label">UK Government – Citizen Financial Service – Design Lead</div>
              <ul>
                <li>Redesigned journeys for login, payment, and case modification which increased site traffic by 23%</li>
                <li>Developed fully interactive prototypes for user testing with HTML, CSS, Javascript</li>
              </ul>
            </div>

            <div className="sub-role">
              <div className="label">(Internal) UX Mentorship Programme</div>
              <ul>
                <li>Developed and scaled the programme, collaborating with senior designers to mentor 16 junior colleagues to enhance their UX career prospects</li>
                <li>Delivered UX training for 200+ new joiners to introduce them to the practice</li>
              </ul>
            </div>
          </TimelineItem>
        </TimelineWrapper>

        <SkillsBox>
          <h2>Skills Matrix</h2>

          <SkillRow>
            <div className="level-header">
              <span>Expert</span>
            </div>
            <div className="skill-pills">
              <span>UX/UI Design</span>
              <span>Prototyping</span>
              <span>Figma</span>
              <span>User Research</span>
              <span>Usability Testing</span>
            </div>
          </SkillRow>

          <SkillRow>
            <div className="level-header">
              <span>Advanced</span>
            </div>
            <div className="skill-pills">
              <span>Information Architecture</span>
              <span>Workshop Facilitation</span>
              <span>Git</span>
              <span>ReactJS</span>
              <span>HTML5</span>
              <span>CSS</span>
              <span>JavaScript</span>
            </div>
          </SkillRow>

          <SkillRow>
            <div className="level-header">
              <span>Other</span>
            </div>
            <div className="skill-pills">
              <span>Flask</span>
              <span>Python</span>
              <span>SQL</span>
            </div>
          </SkillRow>
        </SkillsBox>

        <SectionHeader style={{ marginTop: '20px' }}>
          <h2>Certifications</h2>
        </SectionHeader>
        <SimpleBlockList style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '30px', marginBottom: '60px' }}>
          <div className="block-item">
            <h4>NN/g Conference</h4>
            <p>Certified UX Professional – 2021</p>
          </div>
          <div className="block-item">
            <h4>Scrum.org</h4>
            <p>Professional Scrum Master I - 2018</p>
          </div>
        </SimpleBlockList>

        <SectionHeader>
          <h2>Education</h2>
        </SectionHeader>
        <SimpleBlockList style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '30px' }}>
          <div className="block-item">
            <h4>NEXT Academy</h4>
            <p>Full Stack Bootcamp - 2020</p>
          </div>
          <div className="block-item">
            <h4>Smartly Institute</h4>
            <p>MBA - 2019</p>
          </div>
          <div className="block-item">
            <h4>University College London</h4>
            <p>Neuroscience MSc - 2015</p>
          </div>
        </SimpleBlockList>

      </RightContent>
    </PageWrapper>
  );
};

export default About;
